/**
 * Shared types for the transfer reports multi-step modal flow.
 *
 * The modal walks admins through selecting a destination provider player
 * and transferring all scouting data from a manual player to that target.
 * @module features/player-detail/utils/transferReportsTypes
 */

import type { PlayerSearchPlayerFragment } from "@/generated/graphql";

/** Steps in the transfer reports modal state machine */
export type TransferStep =
  | "search"
  | "summary"
  | "confirm"
  | "transferring"
  | "success";

/**
 * Minimal player info needed across transfer flow steps.
 * Derived from PlayerSearchPlayerFragment to avoid coupling
 * every step to the full GraphQL fragment.
 */
export interface TransferTargetPlayer {
  /** Player ID */
  readonly id: string;
  /** Canonical display name — firstName + knownName + lastName (SE-4725) */
  readonly displayName: string;
  /** Primary position abbreviation, if available */
  readonly positionAbbreviation: string | null;
  /** Team name */
  readonly teamName: string | null;
}

/**
 * Derives a TransferTargetPlayer from a PlayerSearchPlayerFragment.
 * Centralises the display-name logic so every step shows the same label.
 * @param player - The full GraphQL player fragment to extract transfer info from
 * @returns A minimal TransferTargetPlayer suitable for the transfer flow UI
 */
export const toTransferTargetPlayer = (
  player: PlayerSearchPlayerFragment
): TransferTargetPlayer => ({
  id: player.id,
  displayName: player?.name ?? "",
  positionAbbreviation: player.positions?.[0]?.abbreviation ?? null,
  teamName: player.team?.name ?? player.teamName ?? null,
});
